import { isApiFailure } from '@agion/ui/api-failure';
import { unifiedApi } from '@/lib/api-client';
// @size-accept:file: The public audit API client remains consolidated while
// endpoint-specific contracts are extracted to focused modules.
import {
  executionRunMetricsOf,
  type ExecutionSummaryResponse,
} from './audit-execution-summary';

import { getRunResults } from './run-result';
import { nextPageOffset } from './paging';
import {
  COMPLIANCE_EXPORT_PAGE,
  ComplianceDecisionTally,
  ComplianceEventTally,
  exportCompleteness,
  exportedExecution,
  exportedMission,
} from './compliance-export';

export type { ExecutionRunMetrics, ExecutionSummary, ExecutionSummaryResponse } from './audit-execution-summary';
export { executionTerminalStatus } from './audit-execution-summary';

/** The server's refusal of a conversation the caller cannot read (agion/Agion#2988). */
export const CONVERSATION_NOT_VIEWABLE = 'conversation_not_viewable';
/** The server's answer when the caller's capacity needs a typed reason (agion/Agion#2988). */
export const REASON_REQUIRED = 'reason_required';
/** The request header that carries the typed reason, percent-encoded UTF-8; never a URL parameter. */
export const OPEN_REASON_HEADER = 'X-Agion-Open-Reason';

/** Whether error is the server's coded refusal to open this conversation. */
export function isConversationNotViewable(error: unknown): boolean {
  return isApiFailure(error) && error.status === 403 && error.code === CONVERSATION_NOT_VIEWABLE;
}

/** Whether error is the server asking for a reason before it opens this conversation. */
export function isReasonRequired(error: unknown): boolean {
  return isApiFailure(error) && error.status === 400 && error.code === REASON_REQUIRED;
}

/**
 * One page of a run's full content from the host that recorded it. `refused`
 * is set only when the server refused the caller this conversation;
 * `reasonRequired` only when the server needs a reason first. Who must give
 * a reason is the server's decision; the console only reports its answer.
 */
export interface HostAuditContentResult {
  items: MissionAuditEntry[];
  total: number;
  hostAvailable: boolean;
  refused?: true;
  reasonRequired?: true;
}

export type ExecutionSummarySort = 'started_at' | 'host' | 'agents' | 'events' | 'tokens' | 'duration' | 'status';

/**
 * Audit Entry - represents a single audit log entry
 */
export interface AuditEntry {
  id: string;
  timestamp: string;
  event_type: string;
  actor?: {
    id: string;
    type: string;
    name: string;
    role?: string;
    session?: string;
  };
  resource?: {
    id: string;
    type: string;
    name: string;
    path?: string;
  };
  action: string;
  outcome: 'success' | 'failure' | 'warning';
  decision?: 'allow' | 'deny' | 'obfuscate' | 'escalate' | 'audit';
  context?: {
    request_id?: string;
    session_id?: string;
    ip_address?: string;
    user_agent?: string;
    trust_score?: number;
    trust_tier?: number;
    policy_match?: string[];
    model_name?: string;
    tokens_total?: number;
    latency_ms?: number;
  };
  metadata?: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'access' | 'change' | 'compliance' | 'security';
  // New fields for governance audit display
  message_preview?: string; // Preview of the LLM message content
  full_message?: string; // Full message content (for expanded view)
  policies_checked?: number; // Number of policies the event was checked against
  trust_score_change?: number; // Change in trust score (-1 to +1, negative = decreased)
  enforcement_action?: 'allow' | 'deny' | 'obfuscate' | 'escalate' | 'audit'; // Governance enforcement action
}

/**
 * Audit Query - parameters for querying audit events
 */
export interface AuditQuery {
  event_types?: string[];
  actor_ids?: string[];
  resource_ids?: string[];
  actions?: string[];
  outcomes?: string[];
  severities?: string[];
  categories?: string[];
  start_time?: string;
  end_time?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

/**
 * Audit Response - paginated response from audit API
 */
export interface AuditResponse {
  data: AuditEntry[];
  pagination?: {
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  };
}

/**
 * Compliance Report - compliance assessment report
 */
export interface ComplianceReport {
  id: string;
  period: {
    start: string;
    end: string;
  };
  standards: string[];
  overall_score: number;
  compliance_level: 'compliant' | 'non-compliant' | 'partial';
  summary: {
    total_events: number;
    compliant_events: number;
    violation_count: number;
    critical_violations: number;
  };
  violations: Array<{
    id: string;
    standard: string;
    rule: string;
    severity: string;
    description: string;
    detected_at: string;
    status: string;
  }>;
  generated_at: string;
}

/**
 * Execution Audit Entry - represents execution audit data from shared.execution_audit
 * Note: Field names are in camelCase because api-client converts snake_case to camelCase
 */
export interface ExecutionAuditEntry {
  id: string;
  organizationId: string;
  correlationId: string;
  sessionId?: string;
  /** Absent when the decision was recorded without a mission. */
  missionId?: string;
  taskId?: string;
  userId?: string;
  userRole?: string;
  agentId: string;
  agentName?: string;
  agentTrustTier: number;
  action: string;
  resource: string;
  decision: string;
  decisionReason?: string;
  appliedRule?: string;
  trustScoreRequired?: number;
  providerName?: string;
  modelName?: string;
  tokensInput?: number;
  tokensOutput?: number;
  latencyMs?: number;
  requestIp?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

/**
 * Mission Audit Entry - structural audit entry from BYOH host (P-041)
 * Contains ONLY structural metadata - no customer content.
 * Note: Field names are in camelCase because api-client converts snake_case to camelCase
 */
/**
 * AgentKind discriminates between real agent executions and system-generated events.
 * - 'agent': Actual agent turn/tool call with a real user context
 * - 'system': Auto-repair, design generation, or other system-initiated events
 */
export type AgentKind = 'agent' | 'system';

export interface MissionAuditEntry {
  id: string;
  createdAt: string;
  // Nullable: control-plane events (mission_design_*, assistant tool calls,
  // resource-admin CRUD) have no Edge host. The audit UI renders these as
  // "Platform" via the getHostName helper.
  hostId: string | null;
  organizationId: string;
  missionId: string;
  executionId: string;
  nodeId?: string;
  agentId: string;
  eventType: string;
  // WP-A2 (V172): Resolved names from v_mission_audit_with_names view
  // Populated by LEFT JOIN to agion.agents and shared.users
  agentName?: string | null;
  agentKind?: AgentKind;
  userDisplayName?: string | null;
  userEmail?: string | null;
  hitlRequestId?: string;
  resourceId?: string;
  toolName?: string;
  modelName?: string;
  tokensInput?: number;
  tokensOutput?: number;
  durationMs?: number;
  success?: boolean;
  errorCategory?: string;
  /**
   * Wire `outcome` of a mission_end row: the run's terminal reason as the
   * runner classified it (terminal_reason.go, e.g. llm_deadline). More
   * specific than errorCategory; read it through runEndingReading.
   */
  outcome?: string;
  decision?: string;
  /**
   * Wire `routing_decision`. On a system_one_decision row: who answered the
   * switch's question, decision_model or agent_model (agion/Agion#3349).
   */
  routingDecision?: string;
  /**
   * Wire `forced_reason` of a completed node_status row: the forced ending the
   * task step finished after (agion/Agion#2460). Read it through
   * knownForcedReason.
   */
  forcedReason?: string;
  /**
   * A capsule read's coverage on its tool_result row (V386, agion/Agion#2396),
   * as agion-mcp reported it: rows delivered, the true matching count (null
   * or absent when it could not be counted) and whether more rows matched.
   * Absent on every other row and on rows from an older runner.
   */
  rowsRead?: number;
  rowsTotal?: number | null;
  rowsTruncated?: boolean;
  trustScore?: number;
  inputSizeBytes?: number;
  outputSizeBytes?: number;
  outputHash?: string;
  // External-effect audit (ADR-055 Slice 1): what the agent did to an external
  // system on a wired call — read vs write, endpoint, status.
  httpMethod?: string;
  targetHost?: string;
  targetPath?: string;
  responseStatus?: number;
  sideEffect?: string;
  toolKind?: string;
  userId?: string;
  // Populated for assistant events (stored directly on the row).
  // Mission events keep content on BYOH and leave these null.
  inputContent?: string;
  outputContent?: string;
  // V127 ScopeFrame v1 columns. Populated by admin-mcp's audit emitter
  // once it threads the resolved frame through to EmitToolCall — until
  // then they remain undefined for assistant rows. Mission rows from
  // before V127 also have these undefined.
  scopeFrameId?: string;
  intentKind?: string;
  deniedByPolicy?: boolean;
  retrievalTraceId?: string;
  sourceAuditEventId?: string;
  sourceChainIndex?: number;
  sourceEventHash?: string;
  contextPackId?: string;
  contextPackHash?: string;
  learningEpisodeId?: string;
  learningEpisodeHash?: string;
  learningEligible?: boolean;
  noContextReason?: string;
  brokerTraceId?: string;
  brokerManifestHash?: string;
  childContextPacks?: Array<{
    hostId?: string;
    traceId?: string;
    contextPackId?: string;
    contextPackHash?: string;
  }>;
  citationHandles?: string[];
  brokerDegradation?: {
    category?: string;
    message?: string;
    [key: string]: unknown;
  };
}

/**
 * Mission Audit Query - parameters for querying mission execution audit entries
 */
export interface MissionAuditQuery {
  missionId?: string;
  executionId?: string;
  agentId?: string;
  hostId?: string;
  eventType?: string;
  // External-effect filters (ADR-055 #8): "show every WRITE", a given target host,
  // or a tool kind (sql / wired_http / slack / ...). Backed by the Slice 1 columns.
  sideEffect?: string;
  toolKind?: string;
  targetHost?: string;
  startTime?: string;
  endTime?: string;
  limit?: number;
  offset?: number;
}

/**
 * Mission Audit Response - paginated response from mission-execution audit API
 */
export interface MissionAuditResponse {
  items: MissionAuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Mission Summary - aggregated stats per mission for audit drill-down (Level 1)
 * Note: Field names are in camelCase because api-client converts snake_case to camelCase
 */
export interface MissionSummary {
  missionId: string;
  missionName?: string;
  executionCount: number;
  totalEvents: number;
  firstSeen: string;
  lastSeen: string;
  successCount: number;
  failureCount: number;
  hitlCount: number;
  totalTokens: number;
  learningEligibleCount: number;
  promotedLearningCount: number;
}

/**
 * Mission Summary Response - paginated response from mission-summaries API
 */
export interface MissionSummaryResponse {
  items: MissionSummary[];
  total: number;
  limit: number;
  offset: number;
  totalTokensUsed: number;
}

/**
 * Assistant Session Summary - aggregated stats for an assistant chat session
 * Sessions are identified by correlation_id and filtered by agent_id LIKE 'assistant:%'
 * Note: Field names are in camelCase because api-client converts snake_case to camelCase
 */
export interface AssistantSessionSummary {
  correlationId: string;
  userId: string;
  memberId?: string; // organization_members.id — used by admin UI /users/:id
  userName?: string;
  startedAt: string;
  endedAt: string;
  toolCallCount: number;
  successCount: number;
  errorCount: number;
  tokensInput: number;
  tokensOutput: number;
  /**
   * The session's ledger calls whose usage was not reported (backend
   * usage_unknown_calls, agion/Agion#2799); absent when there are none or on
   * an older server. Their tokens are not in tokensInput/tokensOutput.
   */
  usageUnknownCalls?: number;
  totalDurationMs: number;
  sourceAuditEventId?: string;
  sourceChainIndex?: number;
  sourceEventHash?: string;
  contextPackId?: string;
  contextPackHash?: string;
  learningEpisodeId?: string;
  learningEpisodeHash?: string;
  learningEligible?: boolean;
  noContextReason?: string;
}

/**
 * Assistant Session Response - paginated response from assistant-sessions API
 */
export interface AssistantSessionResponse {
  items: AssistantSessionSummary[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Designer Session Summary — extends AssistantSessionSummary with event_type,
 * mission_id, and mission_name so the Mission Designer audit tab can show
 * "what happened" and which mission was affected without a detail fetch.
 * Designer rows are one-event-per-execution_id (every emit uses a fresh UUID),
 * so these fields are effectively per-row values, not aggregates.
 */
export interface DesignerSessionSummary extends AssistantSessionSummary {
  eventType?: string;
  missionId?: string;
  missionName?: string;
}

export interface DesignerSessionResponse {
  items: DesignerSessionSummary[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Resource Session Summary — one row per resource-admin audit event.
 * Each event represents an administrative action against a model provider,
 * governance resource, tool, or default-model setting. agent_id LIKE
 * 'resource-admin:%' identifies the family; category/action narrow it
 * down for the table cells.
 *
 * Note: Field names are in camelCase because api-client converts snake_case to camelCase
 */
export interface ResourceSessionSummary extends AssistantSessionSummary {
  eventType?: string;
  category?: string; // 'provider' | 'resource' | 'tool' | 'model'
  action?: string; // 'connected' | 'disconnected' | 'tested' | 'test_failed' | 'created' | 'updated' | 'deleted' | 'default_set' | 'default_cleared'
  resourceId?: string;
  resourceName?: string;
  resourceType?: string; // provider_type, governance resource type, tool category, or model purpose
  errorMessage?: string;
}

export interface ResourceSessionResponse {
  items: ResourceSessionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface LearningAuditEntry {
  id: string;
  createdAt: string;
  learningType: string;
  title?: string;
  situationSummary?: string;
  actionSummary?: string;
  outcomeSummary?: string;
  confidence: number;
  contextPackId?: string;
  contextPackHash?: string;
  sourceAuditEventId?: string;
  sourceChainIndex?: number;
  sourceEventHash?: string;
  learningEpisodeHash?: string;
  contentRef?: string;
  promotedLearning?: Record<string, unknown>;
  sourceEventType?: string;
  sourceMissionId?: string;
  sourceMissionName?: string;
  sourceAgentId?: string;
  sourceToolName?: string;
  sourceSuccess?: boolean;
  // Precedent reuse — the real signal of whether the learning is being
  // applied. Sourced from agion.decision_precedents via the precedent's
  // content_ref. The detail page surfaces these instead of the
  // hard-coded-zero mission_audit_structural counters.
  precedentType?: string;
  lessonLearned?: string;
  usageCount?: number;
  lastUsedAt?: string;
  relevanceScore?: number;
  tags?: string[];
}

/**
 * KPI Stat - single statistic with label, value, and optional unit.
 * Matches the backend KpiStat struct from audit_kpi_repository.go.
 */
export interface KpiStat {
  label: string;
  value: number;
  unit?: string;
}

/**
 * KPI Trends - 7-bucket trend arrays for sparkline rendering.
 * Each array contains daily values for the last 7 days.
 */
export interface KpiTrends {
  stat1: number[];
  stat2: number[];
  stat3: number[];
  stat4: number[];
}

/**
 * KPI Range - time range and bucketing strategy for the KPI data.
 */
export interface KpiRange {
  startTime: string;
  endTime: string;
  bucket: string;
}

/**
 * KPI Response - unified response format for all tab-scoped KPI endpoints.
 * Contains 4 stats, their trend arrays, and the time range.
 */
export interface KpiResponse {
  stat1: KpiStat;
  stat2: KpiStat;
  stat3: KpiStat;
  stat4: KpiStat;
  trends: KpiTrends;
  range: KpiRange;
}

export interface LearningAuditResponse {
  items: LearningAuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * WP-C10: Recent application of a learning episode.
 * Represents a single application of a promoted learning in a mission execution.
 */
export interface RecentApplication {
  id: string;
  missionId: string;
  missionName: string;
  executionId: string;
  outcome: 'success' | 'failure' | 'pending';
  appliedAt: string;
  actorId?: string;
  actorName?: string;
}

/**
 * WP-C10: Learning applications response from WP-A5 endpoint.
 * Contains the learning entry, aggregated counts, and recent applications.
 */
export interface LearningApplicationsResponse {
  /** The learning entry details (null if not found) */
  learning: LearningAuditEntry | null;
  /** Total number of applications of this learning */
  totalApplications: number;
  /** Number of successful applications */
  successCount: number;
  /** Number of failed applications */
  failureCount: number;
  /** Applications in the last 7 days */
  last7dApplications: number;
  /** Recent applications (limit 25) */
  recentApplications: RecentApplication[];
}

/**
 * Mission Cost - aggregate from agion.llm_call_log (V156, canonical source).
 * Returned by GET /v1/governance/audit/mission-cost.
 *
 * costKnownPct surfaces the data-completeness honestly: a mission whose calls
 * don't all carry a cost_usd (e.g. legacy providers, pre-V167 rows) shouldn't
 * pretend its total is whole truth. The UI shows the percentage alongside the
 * cost number so the reader knows what they're looking at.
 */
export interface ModelCostRow {
  providerType: string;
  modelId: string;
  callCount: number;
  tokensInput: number;
  tokensOutput: number;
  costUsd: number;
  /** Calls of this model whose usage was not reported (backend usage_unknown_calls). */
  usageUnknownCalls?: number;
}

export interface MissionCost {
  missionId: string;
  callCount: number;
  tokensInput: number;
  tokensOutput: number;
  tokensTotal: number;
  /**
   * The priced calls' sum; null when governance serves it as unknown (no
   * counted call reported usage, agion/Agion#2799), never read as $0.
   */
  costUsd: number | null;
  costKnownPct: number;
  /**
   * Calls whose provider did not report usage (backend usage_unknown_calls,
   * agion/Agion#2799): in callCount, unpriced, and adding no token. 0 on an
   * older server. Served by governance, never counted here.
   */
  usageUnknownCalls: number;
  byModel: ModelCostRow[];
  windowStart?: string;
  windowEnd?: string;
}

/**
 * Mission criteria verdicts — content-free projection of
 * agion.mission_criteria_verdicts (V196) + the V197 rate view. Returned by
 * GET /v1/governance/audit/mission-criteria-verdicts (mission-intent-fitness
 * ADR F8). No evidence text crosses to the platform (ADR-055); only status,
 * producer, and aggregate counts/rates.
 */
export type CriterionVerdictStatus = 'met' | 'not_met' | 'unverifiable' | 'unreported';

export interface CriterionVerdictRow {
  criterionIndex: number;
  status: CriterionVerdictStatus;
  producedBy: string;
  missionVersion?: string;
}

export interface CriterionRateRow {
  criterionIndex: number;
  missionVersion?: string;
  totalRuns: number;
  metCount: number;
  notMetCount: number;
  unverifiableCount: number;
  unreportedCount: number;
  /** null = never decisively judged ("never measured"), distinct from 0 ("always failing"). */
  metRate: number | null;
  qualityDefectRate: number;
}

export interface MissionCriteriaVerdicts {
  missionId: string;
  executionId?: string;
  verdicts: CriterionVerdictRow[];
  rates: CriterionRateRow[];
}

/**
 * A mission as its audit view names it: GET /v1/governance/audit/mission-detail
 * (agion/Agion#3048, challenge 2026-10-12-audit-mission-detail-3048). Audit
 * roles read it for every mission of the organisation, so the view no longer
 * depends on the mission routes, which answer only missions the reader is
 * invited to. The verdicts and rates are those of mission-criteria-verdicts.
 */
export interface AuditMissionDetail extends MissionCriteriaVerdicts {
  missionName: string | null;
  /** The mission's current criteria texts (index = criterionIndex); null when not served. */
  successCriteria: string[] | null;
  /**
   * Step id to the label its author gave (backend step_labels,
   * agion/Agion#3432), for readers the mission route refuses; null when not
   * served.
   */
  stepLabels: Record<string, string> | null;
}

/** The served step labels with text values only; null when none is served. */
function readStepLabels(value: unknown): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const labels: Record<string, string> = {};
  for (const [id, label] of Object.entries(value as Record<string, unknown>)) {
    if (typeof label === 'string' && label) labels[id] = label;
  }
  return Object.keys(labels).length > 0 ? labels : null;
}

/**
 * Audit Service - handles audit trail and compliance operations
 */
class AuditService {
  /**
   * Get execution audit events for a specific agent
   * Uses GET /v1/governance/audit/execution which queries shared.execution_audit
   */
  async getAgentAuditTrail(
    agentId: string,
    options?: {
      limit?: number;
      offset?: number;
      category?: string;
      severity?: string;
      outcome?: string;
      search?: string;
      startDate?: Date;
      endDate?: Date;
    }
  ): Promise<AuditResponse> {
    try {
      // Strip DID prefix if present - backend expects plain UUID
      const plainAgentId = agentId.startsWith('did:agion:agent:')
        ? agentId.replace('did:agion:agent:', '')
        : agentId;

      // Build query params for GET /v1/governance/audit/execution
      const searchParams: Record<string, any> = {
        agent_id: plainAgentId,
        limit: options?.limit || 50,
        offset: options?.offset || 0,
      };

      // Add optional filters - map outcome to decision
      if (options?.outcome && options.outcome !== 'all') {
        // Map frontend outcome values to backend decision values
        const decisionMap: Record<string, string> = {
          success: 'allowed',
          failure: 'blocked',
          warning: 'rate_limited',
        };
        searchParams['decision'] = decisionMap[options.outcome] || options.outcome;
      }

      // Add time range filters
      if (options?.startDate) {
        searchParams['start_time'] = options.startDate.toISOString();
      }
      if (options?.endDate) {
        searchParams['end_time'] = options.endDate.toISOString();
      }

      const response = await unifiedApi.get<any>('governance/audit/execution', searchParams);

      // Backend returns { success, data: { entries: [...], count, limit, offset } }
      // Map execution audit entries to frontend AuditEntry format
      // Note: api-client converts snake_case to camelCase automatically
      const entries: ExecutionAuditEntry[] = response.data?.entries || [];
      const mappedEntries: AuditEntry[] = entries.map((entry) => {
        // Get governance action from metadata if available
        // Note: api-client converts snake_case to camelCase, so we check both formats
        const governanceAction =
          (entry.metadata?.['governanceAction'] as string) ||
          (entry.metadata?.['governance_action'] as string) ||
          entry.decision;

        // Extract message content from metadata - check multiple possible field names
        // The execution-service stores message_preview, but api-client may convert to messagePreview
        // Also check legacy fields like response_preview, request_preview, and responsePreview
        // Check both camelCase (after api-client conversion) and snake_case (if not converted)
        const messageContent =
          (entry.metadata?.['messagePreview'] as string) ||
          (entry.metadata?.['message_preview'] as string) ||
          (entry.metadata?.['responsePreview'] as string) ||
          (entry.metadata?.['response_preview'] as string) ||
          (entry.metadata?.['requestPreview'] as string) ||
          (entry.metadata?.['request_preview'] as string) ||
          (entry.metadata?.['messageContent'] as string) ||
          (entry.metadata?.['message_content'] as string) ||
          (entry.metadata?.['requestContent'] as string) ||
          (entry.metadata?.['request_content'] as string) ||
          (entry.metadata?.['prompt'] as string) ||
          '';
        const messagePreview = messageContent ? messageContent.substring(0, 100) : '';

        // Get policy count from metadata - chat-service stores this as 'policies_checked'
        // Also check for matched_policies array as fallback
        // Check both camelCase and snake_case for compatibility
        const policyMatch =
          (entry.metadata?.['matchedPolicies'] as string[]) ||
          (entry.metadata?.['matched_policies'] as string[]) ||
          (entry.appliedRule ? [entry.appliedRule] : []);
        // Explicitly convert to number to avoid string "0" issues
        const rawPoliciesChecked =
          entry.metadata?.['policiesChecked'] ?? entry.metadata?.['policies_checked'];
        const policiesChecked =
          typeof rawPoliciesChecked === 'number'
            ? rawPoliciesChecked
            : typeof rawPoliciesChecked === 'string'
              ? parseInt(rawPoliciesChecked, 10) || 0
              : policyMatch.length || 0;

        // Get trust score change from metadata - chat-service stores this as 'trust_score_change'
        // This is a float value like 0.01 (representing +1% change)
        // Check both camelCase and snake_case for compatibility
        const rawTrustScoreChange =
          entry.metadata?.['trustScoreChange'] ?? entry.metadata?.['trust_score_change'];
        const trustScoreChange =
          typeof rawTrustScoreChange === 'number'
            ? rawTrustScoreChange
            : typeof rawTrustScoreChange === 'string'
              ? parseFloat(rawTrustScoreChange) || 0
              : 0;

        return {
          id: entry.id,
          timestamp: entry.createdAt,
          event_type: entry.action,
          actor: {
            id: entry.agentId,
            type: 'agent',
            // Try top-level agentName first, then check metadata (camelCase from api-client, or snake_case)
            name:
              entry.agentName ||
              (entry.metadata?.['agentName'] as string) ||
              (entry.metadata?.['agent_name'] as string) ||
              'Unknown Agent',
            role: entry.userRole,
            session: entry.sessionId,
          },
          resource: {
            id: entry.resource,
            type: entry.resource?.split(':')[0] || 'unknown',
            name: entry.resource,
            path: entry.resource,
          },
          action: this.formatGovernanceAction(governanceAction),
          outcome: this.mapDecisionToOutcome(entry.decision),
          decision: this.normalizeDecision(entry.decision),
          context: {
            request_id: entry.correlationId,
            session_id: entry.sessionId,
            ip_address: entry.requestIp,
            user_agent: entry.userAgent,
            trust_score: entry.trustScoreRequired,
            trust_tier: entry.agentTrustTier,
            policy_match: policyMatch,
            model_name: entry.modelName,
            tokens_total: (entry.tokensInput || 0) + (entry.tokensOutput || 0),
            latency_ms: entry.latencyMs,
          },
          metadata: {
            ...entry.metadata,
            providerName: entry.providerName,
            modelName: entry.modelName,
            tokensInput: entry.tokensInput,
            tokensOutput: entry.tokensOutput,
            latencyMs: entry.latencyMs,
            decisionReason: entry.decisionReason,
            responsePreview:
              entry.metadata?.['responsePreview'] || entry.metadata?.['response_preview'],
          },
          // Use governance action to determine severity (more meaningful than trust tier)
          severity: this.mapDecisionToSeverity(entry.decision),
          category: 'access',
          // New governance audit fields
          message_preview: messagePreview,
          full_message: messageContent,
          policies_checked: policiesChecked,
          trust_score_change: trustScoreChange,
          enforcement_action: this.normalizeDecision(entry.decision),
        };
      });

      return {
        data: mappedEntries,
        pagination: {
          total: response.data?.count || entries.length,
          page: Math.floor((options?.offset || 0) / (options?.limit || 50)) + 1,
          page_size: options?.limit || 50,
          total_pages: Math.ceil((response.data?.count || entries.length) / (options?.limit || 50)),
        },
      };
    } catch (error) {
      console.error('Failed to fetch agent audit trail:', error);
      return { data: [] };
    }
  }

  /**
   * Format governance action for display
   */
  private formatGovernanceAction(action: string): string {
    if (!action) return 'Execute';

    switch (action.toLowerCase()) {
      case 'allow':
      case 'allowed':
        return 'Allow';
      case 'deny':
      case 'blocked':
        return 'Deny';
      case 'obfuscate':
        return 'Obfuscate';
      case 'escalate':
        return 'Escalate';
      default:
        return action.charAt(0).toUpperCase() + action.slice(1);
    }
  }

  /**
   * Map backend decision to frontend outcome for visual status
   */
  private mapDecisionToOutcome(decision: string): 'success' | 'failure' | 'warning' {
    switch (decision?.toLowerCase()) {
      case 'allow':
      case 'allowed':
        return 'success';
      case 'deny':
      case 'blocked':
        return 'failure';
      case 'obfuscate':
      case 'escalate':
      case 'escalated':
      case 'rate_limited':
        return 'warning';
      default:
        return 'success';
    }
  }

  /**
   * Normalize decision to standard governance actions
   */
  private normalizeDecision(
    decision: string
  ): 'allow' | 'deny' | 'obfuscate' | 'escalate' | 'audit' {
    switch (decision?.toLowerCase()) {
      case 'allow':
      case 'allowed':
        return 'allow';
      case 'deny':
      case 'blocked':
        return 'deny';
      case 'obfuscate':
        return 'obfuscate';
      case 'escalate':
      case 'escalated':
        return 'escalate';
      case 'audit':
        return 'audit';
      default:
        return 'allow';
    }
  }

  /**
   * Map governance decision/action to severity for display
   * This provides more meaningful severity based on what action was taken
   */
  private mapDecisionToSeverity(decision: string): 'low' | 'medium' | 'high' | 'critical' {
    switch (decision?.toLowerCase()) {
      case 'allow':
      case 'allowed':
        return 'low'; // Normal operation - low severity
      case 'obfuscate':
        return 'medium'; // Data was modified - medium severity
      case 'escalate':
        return 'high'; // Human review needed - high severity
      case 'deny':
      case 'blocked':
        return 'critical'; // Access denied - critical severity
      default:
        return 'low'; // Default to low for unknown
    }
  }

  /**
   * Get filtered audit events
   * Uses the execution audit endpoint which is backed by the database (shared.execution_audit)
   */
  async queryAuditEvents(query: AuditQuery): Promise<AuditResponse> {
    try {
      // Backend endpoint is GET /v1/governance/audit/execution
      // This is backed by PostgreSQL shared.execution_audit table
      const searchParams: Record<string, any> = {
        limit: query.limit || 50,
        offset: query.offset || 0,
      };

      // Add outcome filter - map to decision
      if (query.outcomes && query.outcomes.length > 0) {
        const decisionMap: Record<string, string> = {
          success: 'allowed',
          failure: 'blocked',
          warning: 'rate_limited',
        };
        const outcome = query.outcomes[0];
        if (outcome) {
          searchParams['decision'] = decisionMap[outcome] || outcome;
        }
      }

      // Add time range filters
      if (query.start_time) {
        searchParams['start_time'] = query.start_time;
      }
      if (query.end_time) {
        searchParams['end_time'] = query.end_time;
      }

      const response = await unifiedApi.get<any>('governance/audit/execution', searchParams);

      // Backend returns { success, data: { entries: [...], count, limit, offset } }
      // Map execution audit entries to frontend AuditEntry format
      const entries: ExecutionAuditEntry[] = response.data?.entries || [];
      const totalCount = response.data?.count || entries.length;

      const mappedEntries: AuditEntry[] = entries.map((entry) => {
        // Get governance action from metadata if available
        // Note: api-client converts snake_case to camelCase, so we check both formats
        const governanceAction =
          (entry.metadata?.['governanceAction'] as string) ||
          (entry.metadata?.['governance_action'] as string) ||
          entry.decision;

        // Extract message content from metadata - check multiple possible field names
        // The execution-service stores message_preview, but api-client may convert to messagePreview
        // Also check legacy fields like response_preview, request_preview, and responsePreview
        // Check both camelCase (after api-client conversion) and snake_case (if not converted)
        const messageContent =
          (entry.metadata?.['messagePreview'] as string) ||
          (entry.metadata?.['message_preview'] as string) ||
          (entry.metadata?.['responsePreview'] as string) ||
          (entry.metadata?.['response_preview'] as string) ||
          (entry.metadata?.['requestPreview'] as string) ||
          (entry.metadata?.['request_preview'] as string) ||
          (entry.metadata?.['messageContent'] as string) ||
          (entry.metadata?.['message_content'] as string) ||
          (entry.metadata?.['requestContent'] as string) ||
          (entry.metadata?.['request_content'] as string) ||
          (entry.metadata?.['prompt'] as string) ||
          '';
        const messagePreview = messageContent ? messageContent.substring(0, 100) : '';

        // Get policy count from metadata - chat-service stores this as 'policies_checked'
        // Also check for matched_policies array as fallback
        // Check both camelCase and snake_case for compatibility
        const policyMatch =
          (entry.metadata?.['matchedPolicies'] as string[]) ||
          (entry.metadata?.['matched_policies'] as string[]) ||
          (entry.appliedRule ? [entry.appliedRule] : []);
        // Explicitly convert to number to avoid string "0" issues
        const rawPoliciesChecked =
          entry.metadata?.['policiesChecked'] ?? entry.metadata?.['policies_checked'];
        const policiesChecked =
          typeof rawPoliciesChecked === 'number'
            ? rawPoliciesChecked
            : typeof rawPoliciesChecked === 'string'
              ? parseInt(rawPoliciesChecked, 10) || 0
              : policyMatch.length || 0;

        // Get trust score change from metadata - chat-service stores this as 'trust_score_change'
        // This is a float value like 0.01 (representing +1% change)
        // Check both camelCase and snake_case for compatibility
        const rawTrustScoreChange =
          entry.metadata?.['trustScoreChange'] ?? entry.metadata?.['trust_score_change'];
        const trustScoreChange =
          typeof rawTrustScoreChange === 'number'
            ? rawTrustScoreChange
            : typeof rawTrustScoreChange === 'string'
              ? parseFloat(rawTrustScoreChange) || 0
              : 0;

        return {
          id: entry.id,
          timestamp: entry.createdAt,
          event_type: entry.action,
          actor: {
            id: entry.agentId,
            type: 'agent',
            // Try top-level agentName first, then check metadata (camelCase from api-client, or snake_case)
            name:
              entry.agentName ||
              (entry.metadata?.['agentName'] as string) ||
              (entry.metadata?.['agent_name'] as string) ||
              'Unknown Agent',
            role: entry.userRole,
            session: entry.sessionId,
          },
          resource: {
            id: entry.resource,
            type: entry.resource?.split(':')[0] || 'unknown',
            name: entry.resource,
            path: entry.resource,
          },
          action: this.formatGovernanceAction(governanceAction),
          outcome: this.mapDecisionToOutcome(entry.decision),
          decision: this.normalizeDecision(entry.decision),
          context: {
            request_id: entry.correlationId,
            session_id: entry.sessionId,
            ip_address: entry.requestIp,
            user_agent: entry.userAgent,
            trust_score: entry.trustScoreRequired,
            trust_tier: entry.agentTrustTier,
            policy_match: policyMatch,
            model_name: entry.modelName,
            tokens_total: (entry.tokensInput || 0) + (entry.tokensOutput || 0),
            latency_ms: entry.latencyMs,
          },
          metadata: {
            ...entry.metadata,
            providerName: entry.providerName,
            modelName: entry.modelName,
            tokensInput: entry.tokensInput,
            tokensOutput: entry.tokensOutput,
            latencyMs: entry.latencyMs,
            decisionReason: entry.decisionReason,
            responsePreview:
              entry.metadata?.['responsePreview'] || entry.metadata?.['response_preview'],
          },
          // Use governance action to determine severity (more meaningful than trust tier)
          severity: this.mapDecisionToSeverity(entry.decision),
          category: 'access',
          // New governance audit fields
          message_preview: messagePreview,
          full_message: messageContent,
          policies_checked: policiesChecked,
          trust_score_change: trustScoreChange,
          enforcement_action: this.normalizeDecision(entry.decision),
        };
      });

      return {
        data: mappedEntries,
        pagination: {
          total: totalCount,
          page: Math.floor((query.offset || 0) / (query.limit || 50)) + 1,
          page_size: query.limit || 50,
          total_pages: Math.ceil(totalCount / (query.limit || 50)),
        },
      };
    } catch (error) {
      console.error('Failed to query audit events:', error);
      return { data: [] };
    }
  }

  /**
   * Get compliance report for an entity
   * NOTE: Backend endpoint /audit/compliance/{id} does not exist
   * This is a placeholder that returns mock data for UI compatibility
   */
  async getComplianceReport(
    _entityId: string,
    _period: '7d' | '30d' | '90d' = '30d'
  ): Promise<ComplianceReport | null> {
    console.warn('Compliance report endpoint not implemented in backend');

    // Return null as endpoint doesn't exist
    // UI should handle gracefully
    return null;

    // TODO: Implement backend endpoint at /v1/audit/compliance/{entityId}
    // or remove compliance report feature from UI
  }

  /**
   * Log an audit event (for manual audit trail creation)
   * NOTE: Backend endpoint POST /audit/events does not exist
   * Audit events are logged automatically by backend middleware
   * This method is kept for API compatibility but does nothing
   */
  async logEvent(_event: {
    event_type: string;
    entity_id?: string;
    entity_type?: string;
    user_id: string;
    action: string;
    resource: string;
    result: string;
    details?: Record<string, any>;
    severity?: string;
  }): Promise<boolean> {
    console.warn(
      'Manual audit event logging not supported - events are logged automatically by backend'
    );

    // Return true as events are logged automatically by backend middleware
    // No manual logging endpoint is needed
    return true;

    // TODO: If manual audit logging is required, add POST /v1/audit/events endpoint
    // or remove manual logging calls from UI
  }

  /**
   * Query mission execution audit entries (P-041, WP-004)
   * Uses GET /v1/governance/audit/mission-execution which queries shared.mission_audit_structural
   * These are content-stripped structural entries from BYOH hosts.
   */
  async queryMissionAudit(query: MissionAuditQuery): Promise<MissionAuditResponse> {
    const searchParams: Record<string, any> = {
      limit: query.limit || 50,
      offset: query.offset || 0,
    };

    if (query.missionId) searchParams['mission_id'] = query.missionId;
    if (query.executionId) searchParams['execution_id'] = query.executionId;
    if (query.agentId) searchParams['agent_id'] = query.agentId;
    if (query.hostId) searchParams['host_id'] = query.hostId;
    if (query.eventType) searchParams['event_type'] = query.eventType;
    if (query.sideEffect) searchParams['side_effect'] = query.sideEffect;
    if (query.toolKind) searchParams['tool_kind'] = query.toolKind;
    if (query.targetHost) searchParams['target_host'] = query.targetHost;
    if (query.startTime) searchParams['start_time'] = query.startTime;
    if (query.endTime) searchParams['end_time'] = query.endTime;

    const response = await unifiedApi.get<any>('governance/audit/mission-execution', searchParams);

    // Backend returns { success, data: { items, total, limit, offset }, timestamp }
    const data = response.data || {};
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: data.total || 0,
      limit: data.limit || query.limit || 50,
      offset: data.offset || query.offset || 0,
    };
  }

  /**
   * Get mission execution audit entries for a specific execution (P-041, WP-004)
   * Uses GET /v1/governance/audit/mission-execution/{executionId}
   */
  async getExecutionMissionAudit(executionId: string): Promise<MissionAuditEntry[]> {
    try {
      const response = await unifiedApi.get<any>(
        `governance/audit/mission-execution/${executionId}`
      );

      const data = response.data || {};
      return Array.isArray(data.items) ? data.items : [];
    } catch (error) {
      console.error('Failed to get execution mission audit:', error);
      return [];
    }
  }

  /**
   * Get mission execution audit entries for a specific agent (P-041, WP-005)
   * Queries the mission-execution endpoint filtered by agent_id
   */
  async getAgentMissionAudit(
    agentId: string,
    options?: { limit?: number; offset?: number; startTime?: string; endTime?: string }
  ): Promise<MissionAuditResponse> {
    return this.queryMissionAudit({
      agentId,
      limit: options?.limit || 25,
      offset: options?.offset || 0,
      startTime: options?.startTime,
      endTime: options?.endTime,
    });
  }

  /**
   * Get mission execution audit entries for a specific host (P-041, WP-005)
   * Queries the mission-execution endpoint filtered by host_id
   */
  async getHostMissionAudit(
    hostId: string,
    options?: { limit?: number; offset?: number; startTime?: string; endTime?: string }
  ): Promise<MissionAuditResponse> {
    return this.queryMissionAudit({
      hostId,
      limit: options?.limit || 25,
      offset: options?.offset || 0,
      startTime: options?.startTime,
      endTime: options?.endTime,
    });
  }

  /**
   * Get full audit events from BYOH host via VPN proxy (P-041 DD-5).
   * Returns complete entries including content (PII) from the BYOH local store.
   * Falls back gracefully if host is offline. When the server refuses the
   * caller this conversation (403 conversation_not_viewable, agion/Agion#2988)
   * the answer is `refused`; when it needs a reason first (400
   * reason_required) the answer is `reasonRequired`, and the caller asks the
   * person and reads again with `reason`, sent as the X-Agion-Open-Reason
   * header. Both keep `hostAvailable` false for callers that read neither.
   */
  async getFullAuditFromHost(
    hostId: string,
    executionId: string,
    options?: { limit?: number; offset?: number; eventType?: string; reason?: string }
  ): Promise<HostAuditContentResult> {
    try {
      const params: Record<string, string> = {};
      if (options?.limit) params['limit'] = String(options.limit);
      if (options?.offset) params['offset'] = String(options.offset);
      if (options?.eventType) params['event_type'] = options.eventType;

      const response = await unifiedApi.get<any>(
        `governance/agent-hosts/${hostId}/audit/executions/${executionId}/events`,
        params,
        options?.reason ? { headers: { [OPEN_REASON_HEADER]: encodeURIComponent(options.reason) } } : undefined
      );

      const data = response.data || response;
      const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data.events)
          ? data.events
          : [];
      return {
        items,
        total: typeof data.total === 'number' ? data.total : items.length,
        hostAvailable: true,
      };
    } catch (error: any) {
      if (isConversationNotViewable(error)) {
        return { items: [], total: 0, hostAvailable: false, refused: true };
      }
      if (isReasonRequired(error)) {
        return { items: [], total: 0, hostAvailable: false, reasonRequired: true };
      }
      // 502 = host unreachable via VPN, 404 = host not found
      if (error?.status === 502 || error?.status === 404) {
        return { items: [], total: 0, hostAvailable: false };
      }
      console.error('Failed to get full audit from host:', error);
      return { items: [], total: 0, hostAvailable: false };
    }
  }

  /**
   * Export audit events from BYOH host for compliance (P-041 DD-5).
   * Triggers a JSON file download via the VPN proxy.
   */
  async exportAuditFromHost(hostId: string, executionId: string): Promise<void> {
    try {
      const response = await unifiedApi.get<any>(`governance/agent-hosts/${hostId}/audit/export`, {
        execution_id: executionId,
      });

      // Trigger file download

      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;

      a.download = `audit-export-${executionId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export audit from host:', error);
      throw error;
    }
  }

  /**
   * Query mission summaries for audit drill-down Level 1 (Mission list)
   * Uses GET /v1/governance/audit/mission-summaries
   */
  async queryMissionSummaries(query: {
    /** A failed read throws instead of reading as an empty list (the next pages, ADR-20260925). */
    throwOnError?: boolean;
    startTime?: string;
    endTime?: string;
    limit?: number;
    offset?: number;
    /** Whitelisted column: lastRun | firstSeen | executions | events | tokens | successRate */
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** List platform-managed missions too (agion/Agion#2650); off by default. */
    includePlatformManaged?: boolean;
  }): Promise<MissionSummaryResponse> {
    try {
      const searchParams: Record<string, any> = {
        limit: query.limit || 50,
        offset: query.offset || 0,
      };

      if (query.startTime) searchParams['start_time'] = query.startTime;
      if (query.endTime) searchParams['end_time'] = query.endTime;
      if (query.sortBy) searchParams['sort_by'] = query.sortBy;
      if (query.sortOrder) searchParams['sort_order'] = query.sortOrder;
      if (query.includePlatformManaged) searchParams['include_platform_managed'] = 'true';

      const response = await unifiedApi.get<any>(
        'governance/audit/mission-summaries',
        searchParams
      );

      // Governance service wraps in {success, data, timestamp} — unwrap if present
      const data = response.data || response;
      return {
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total || 0,
        limit: data.limit || query.limit || 50,
        offset: data.offset || query.offset || 0,
        totalTokensUsed: data.totalTokensUsed || 0,
      };
    } catch (error) {
      if (query.throwOnError) throw error;
      console.error('Failed to query mission summaries:', error);
      return { items: [], total: 0, limit: 50, offset: 0, totalTokensUsed: 0 };
    }
  }

  /**
   * Query execution summaries for audit drill-down Level 2 (Execution list)
   * Uses GET /v1/governance/audit/execution-summaries?mission_id=X
   */
  async queryExecutionSummaries(query: {
    missionId: string;
    startTime?: string;
    endTime?: string;
    limit?: number;
    offset?: number;
    q?: string;
    sortBy?: ExecutionSummarySort;
    sortOrder?: 'asc' | 'desc';
    /** New consumers can distinguish request failures from an empty population. */
    throwOnError?: boolean;
    /** Ask for each run's result verdict (agion/Agion#2395); without it the request is today's. */
    includeResultVerdict?: boolean;
    /** Ask for run_metrics.last_failure, the period's newest failed run (agion/Agion#2515); without it the request is today's. */
    includeLastFailure?: boolean;
    /**
     * One run of the mission by its execution id (agion/Agion#2395): the
     * server's audit gate checks the run, and items, total and run metrics
     * then describe only that run. Without it the request is today's.
     */
    executionId?: string;
  }): Promise<ExecutionSummaryResponse> {
    try {
      const searchParams: Record<string, any> = {
        mission_id: query.missionId,
        ...(query.executionId ? { execution_id: query.executionId } : {}),
        limit: query.limit || 50,
        offset: query.offset || 0,
      };

      if (query.startTime) searchParams['start_time'] = query.startTime;
      if (query.endTime) searchParams['end_time'] = query.endTime;
      if (query.q) searchParams['q'] = query.q;
      if (query.sortBy) searchParams['sort_by'] = query.sortBy;
      if (query.sortOrder) searchParams['sort_order'] = query.sortOrder;
      if (query.includeResultVerdict) searchParams['include_result_verdict'] = 'true';
      if (query.includeLastFailure) searchParams['include_last_failure'] = 'true';

      const response = await unifiedApi.get<any>(
        'governance/audit/execution-summaries',
        searchParams
      );

      const data = response.data || {};
      // An older service may return HTTP 200 while ignoring these additive
      // parameters. Never present that unfiltered page as a successful search.
      const requiresSearchSupport = Boolean(query.q?.trim())
        || (query.sortBy !== undefined && query.sortBy !== 'started_at')
        || (query.sortOrder !== undefined && query.sortOrder !== 'desc');
      if (query.throwOnError && requiresSearchSupport
          && data.searchSupported !== true && data.search_supported !== true) {
        throw new Error('Search and sorting are not available yet. Please try again.');
      }

      return {
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total || 0,
        limit: data.limit || query.limit || 50,
        offset: data.offset || query.offset || 0,
        missionId: data.missionId || query.missionId,
        runMetrics: executionRunMetricsOf(data.runMetrics ?? data.run_metrics),
      };
    } catch (error) {
      if (query.throwOnError) throw error;
      console.error('Failed to query execution summaries:', error);
      return { items: [], total: 0, limit: 50, offset: 0, missionId: query.missionId };
    }
  }

  /**
   * Get mission cost — sums from agion.llm_call_log (V156 canonical, the
   * only source with cost_usd). mission_audit_structural cannot answer this
   * question; it only carries token counts, no cost.
   *
   * Returns a zero-valued object on error so the UI can render "—" instead
   * of crashing on undefined access. Console-warns the error for diagnosis.
   */
  async getMissionCost(query: {
    missionId: string;
    /** Scope the aggregation to one run (backend execution_id, mission-intent-fitness F3). */
    executionId?: string;
    startTime?: string;
    endTime?: string;
    /** A failed read throws instead of reading as no spend (the next pages, ADR-20260925). */
    throwOnError?: boolean;
  }): Promise<MissionCost> {
    const empty: MissionCost = {
      missionId: query.missionId,
      callCount: 0,
      tokensInput: 0,
      tokensOutput: 0,
      tokensTotal: 0,
      costUsd: 0,
      costKnownPct: 0,
      usageUnknownCalls: 0,
      byModel: [],
    };
    try {
      const params: Record<string, string> = { mission_id: query.missionId };
      if (query.executionId) params.execution_id = query.executionId;
      if (query.startTime) params.start_time = query.startTime;
      if (query.endTime) params.end_time = query.endTime;
      const response = await unifiedApi.get<any>('governance/audit/mission-cost', params);
      const data = response.data;
      if (!data || typeof data !== 'object') return empty;
      return {
        missionId: data.missionId ?? query.missionId,
        callCount: data.callCount ?? 0,
        tokensInput: data.tokensInput ?? 0,
        tokensOutput: data.tokensOutput ?? 0,
        tokensTotal: data.tokensTotal ?? 0,
        costUsd: data.costUsd === null ? null : (data.costUsd ?? 0),
        costKnownPct: data.costKnownPct ?? 0,
        usageUnknownCalls: data.usageUnknownCalls ?? 0,
        byModel: Array.isArray(data.byModel) ? data.byModel : [],
        windowStart: data.windowStart,
        windowEnd: data.windowEnd,
      };
    } catch (error) {
      if (query.throwOnError) throw error;
      console.warn('Failed to query mission cost:', error);
      return empty;
    }
  }

  /**
   * Mission criteria verdicts (mission-intent-fitness ADR F8). Content-free
   * per-criterion outcome vector for one execution (when executionId is given)
   * plus the per-criterion met-rate aggregates across the mission's runs.
   *
   * Returns an empty object on error so the Success Criteria card renders "—"
   * instead of crashing on undefined access.
   */
  async getMissionCriteriaVerdicts(query: {
    missionId: string;
    executionId?: string;
    /**
     * Bound the rates to the runs the execution summaries list for the same
     * period (agion/Agion#2524); without them the rates are all-time.
     */
    startTime?: string;
    endTime?: string;
    /** A failed read throws instead of reading as no verdicts (the next pages, ADR-20260925). */
    throwOnError?: boolean;
  }): Promise<MissionCriteriaVerdicts> {
    const empty: MissionCriteriaVerdicts = {
      missionId: query.missionId,
      executionId: query.executionId,
      verdicts: [],
      rates: [],
    };
    try {
      const params: Record<string, string> = { mission_id: query.missionId };
      if (query.executionId) params.execution_id = query.executionId;
      if (query.startTime) params.start_time = query.startTime;
      if (query.endTime) params.end_time = query.endTime;
      const response = await unifiedApi.get<any>('governance/audit/mission-criteria-verdicts', params);
      const data = response.data;
      if (!data || typeof data !== 'object') return empty;
      return {
        missionId: data.missionId ?? query.missionId,
        executionId: data.executionId,
        verdicts: Array.isArray(data.verdicts) ? data.verdicts : [],
        rates: Array.isArray(data.rates) ? data.rates : [],
      };
    } catch (error) {
      if (query.throwOnError) throw error;
      console.warn('Failed to query mission criteria verdicts:', error);
      return empty;
    }
  }

  /**
   * The mission an audit view shows, with its criteria verdicts and rates
   * (agion/Agion#3048). startTime and endTime bound the rates to the runs the
   * execution summaries list for the same period; without them they are
   * all-time. A failed read throws, so a caller never reads it as a mission
   * without a name.
   */
  async getMissionDetail(query: {
    missionId: string;
    executionId?: string;
    startTime?: string;
    endTime?: string;
  }): Promise<AuditMissionDetail> {
    const params: Record<string, string> = { mission_id: query.missionId };
    if (query.executionId) params.execution_id = query.executionId;
    if (query.startTime) params.start_time = query.startTime;
    if (query.endTime) params.end_time = query.endTime;
    const response = await unifiedApi.get<any>('governance/audit/mission-detail', params);
    const data = response?.data && typeof response.data === 'object' ? response.data : {};
    const texts = Array.isArray(data.successCriteria) ? data.successCriteria : null;
    return {
      missionId: typeof data.missionId === 'string' && data.missionId ? data.missionId : query.missionId,
      executionId: data.executionId || undefined,
      missionName: typeof data.missionName === 'string' && data.missionName ? data.missionName : null,
      successCriteria: texts ? texts.map((text: unknown) => (typeof text === 'string' ? text : '')) : null,
      stepLabels: readStepLabels(data.stepLabels),
      verdicts: Array.isArray(data.verdicts) ? data.verdicts : [],
      rates: Array.isArray(data.rates) ? data.rates : [],
    };
  }

  /**
   * Get execution audit stats (from shared.execution_audit)
   * Used by audit page for human interactions count
   */
  async getExecutionStats(options?: {
    startTime?: string;
    endTime?: string;
  }): Promise<{ humanEventsCount: number }> {
    try {
      const searchParams: Record<string, string> = {};
      if (options?.startTime) searchParams['start_time'] = options.startTime;
      if (options?.endTime) searchParams['end_time'] = options.endTime;

      const response = await unifiedApi.get<any>('governance/audit/execution/stats', searchParams);

      const stats = response.data?.stats || {};
      return {
        humanEventsCount: stats.humanEventsCount || 0,
      };
    } catch (error) {
      console.error('Failed to get execution stats:', error);
      return { humanEventsCount: 0 };
    }
  }

  /**
   * Execution audit totals for a time window (governance/audit/
   * execution/stats). Owners, admins and auditors get the organisation's;
   * every other role gets the totals over the missions it can view
   * (PLAN-20260912 slice 64). Unlike getExecutionStats
   * this throws on failure, so a caller can tell a failed read from zero
   * (ADR-20260925). Used by the next Overview (agion/Agion#2582).
   */
  async getExecutionTotals(options: { startTime: string; endTime: string }): Promise<{
    totalEvents: number;
    allowedCount: number;
    blockedCount: number;
    humanEventsCount: number;
  }> {
    const response = await unifiedApi.get<any>('governance/audit/execution/stats', {
      start_time: options.startTime,
      end_time: options.endTime,
    });
    const stats = response?.data?.stats;
    if (!stats || typeof stats.totalEvents !== 'number') throw new Error('Execution audit totals are unavailable');
    return {
      totalEvents: stats.totalEvents,
      allowedCount: stats.allowedCount ?? 0,
      blockedCount: stats.blockedCount ?? 0,
      humanEventsCount: stats.humanEventsCount ?? 0,
    };
  }

  /**
   * Export compliance audit trail as a structured JSON document.
   * Combines two centrally-available data sources:
   *   1. Mission execution events (shared.mission_audit_structural) — what happened
   *   2. Governance decisions (shared.execution_audit) — what was allowed/denied and why
   *
   * Note: Full input/output content lives on BYOH hosts and is available per-event
   * in the UI's expanded view. Bulk content export is not included because it requires
   * VPN access to potentially offline hosts.
   *
   * Every row of the period is read, a page at a time, and counted, so the
   * totals and the run list cover the whole period; the file lists a bounded
   * number of rows and says in `completeness` when it lists fewer than the
   * period holds (agion/Agion#3391, ./compliance-export).
   */
  async exportStructuralAudit(query: {
    missionId?: string;
    /** Limits the run record to one execution; governance decisions stay those of the mission (their endpoint has no execution filter). */
    executionId?: string;
    startTime: string;
    endTime: string;
  }): Promise<void> {
    try {
      // 1. Read every structural audit entry of the period, newest first,
      // until a page comes back empty; each read starts one page on, by the
      // limit the server answered with, so a page that came back short never
      // shifts the next one. A page that repeats the one before it ends the
      // reading and the file says so.
      const events = new ComplianceEventTally();
      let serverEventTotal = 0;
      {
        let previousFirstId: string | undefined;
        for (let offset = 0; ;) {
          const auditQuery: MissionAuditQuery = {
            limit: COMPLIANCE_EXPORT_PAGE,
            offset,
            startTime: query.startTime,
            endTime: query.endTime,
          };
          if (query.missionId) auditQuery.missionId = query.missionId;
          if (query.executionId) auditQuery.executionId = query.executionId;
          const response = await this.queryMissionAudit(auditQuery);
          if (offset === 0) serverEventTotal = response.total;
          if (response.items.length === 0) break;
          const firstId = response.items[0]?.id;
          if (offset > 0 && firstId !== undefined && firstId === previousFirstId) {
            events.readInFull = false;
            break;
          }
          previousFirstId = firstId;
          for (const entry of response.items) events.add(entry);
          offset = nextPageOffset(offset, response.limit, COMPLIANCE_EXPORT_PAGE);
        }
      }

      // 2. Read every governance decision of the period the same way; a
      // mission export counts only that mission's (the endpoint has no
      // mission filter).
      const decisions = new ComplianceDecisionTally(query.missionId);
      {
        let previousFirstId: string | undefined;
        for (let offset = 0; ;) {
          const searchParams: Record<string, string | number> = {
            limit: COMPLIANCE_EXPORT_PAGE,
            offset,
            start_time: query.startTime,
            end_time: query.endTime,
          };
          const response = await unifiedApi.get<{
            data?: { entries?: ExecutionAuditEntry[]; count?: number; limit?: number };
          }>('governance/audit/execution', searchParams);
          const entries = response.data?.entries || [];
          if (entries.length === 0) break;
          const firstId = entries[0]?.id;
          if (offset > 0 && firstId !== undefined && firstId === previousFirstId) {
            decisions.readInFull = false;
            break;
          }
          previousFirstId = firstId;
          for (const entry of entries) decisions.add(entry);
          offset = nextPageOffset(offset, response.data?.limit, COMPLIANCE_EXPORT_PAGE);
        }
      }

      // 3. Build each mission's runs. Each run's result and codes are the
      // server's run end record (agion/Agion#2834); a run without one
      // (switch off, or not in the run list) counts as before.
      let runResultsReadInFull = true;
      const missions = await Promise.all(Array.from(events.missions.entries()).map(async ([missionId, runs]) => {
        const reading = missionId
          ? await getRunResults(missionId, Array.from(runs.keys()))
          : { results: new Map(), readInFull: true };
        if (!reading.readInFull) runResultsReadInFull = false;
        const executions = Array.from(runs.values()).map((run) => exportedExecution(run, reading.results.get(run.executionId)));
        return exportedMission(missionId, executions);
      }));
      const allExecutions = missions.flatMap((m) => m.executions);

      // 4. Summarize governance decisions
      const governanceSummary = decisions.summary();

      // 5. Assemble final compliance document
      const complianceDocument = {
        documentType: 'agion-compliance-audit-trail',
        version: '1.1',
        exportedAt: new Date().toISOString(),
        period: {
          start: query.startTime,
          end: query.endTime,
        },
        scope: query.missionId
          ? { type: 'mission', missionId: query.missionId }
          : { type: 'organization' },
        completeness: exportCompleteness({ events, serverEventTotal, executions: allExecutions, decisions, runResultsReadInFull }),
        summary: {
          totalMissions: missions.length,
          totalExecutions: allExecutions.length,
          totalEvents: events.read,
          totalGovernanceDecisions: decisions.total,
          totalTokensInput: events.tokensInput,
          totalTokensOutput: events.tokensOutput,
          uniqueAgents: events.agents.size,
          uniqueHosts: events.hosts.size,
          humanOversightEvents: events.humanOversightEvents,
          humanRequestEvents: events.humanRequestEvents,
          failedEvents: events.failedEvents,
          deniedActions: decisions.denied,
          escalatedActions: decisions.escalated,
        },
        riskIndicators: {
          hasdeniedActions: decisions.denied > 0,
          hasEscalations: decisions.escalated > 0,
          hasFailedExecutions: missions.some((m) => m.summary.failedExecutions > 0),
          hasHITLOverrides: events.humanOversightEvents > 0,
        },
        governance: governanceSummary,
        missions,
        dataProvenance: {
          executionTimeline: 'shared.mission_audit_structural (platform database)',
          governanceDecisions: query.missionId
            ? 'shared.execution_audit (platform database), the decisions recorded for this mission; a decision recorded without a mission is not included'
            : 'shared.execution_audit (platform database)',
          contentAvailability:
            'Full input/output content is stored on BYOH hosts and viewable per-event in the admin UI. Content is not included in bulk exports as it requires VPN access to individual hosts.',
          humanOversight:
            'Human oversight counts the answers a person gave (hitl_response). Requests to a person are listed per execution under humanRequests and are not counted: the platform record cannot tell a question from an output message, so a question nobody answered is not counted as oversight.',
          listedRows:
            'Totals and execution summaries count every row of the period. Each execution lists eventsListed of its events; completeness states how many rows the file lists against how many the period holds.',
        },
      };

      const blob = new Blob([JSON.stringify(complianceDocument, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;

      const suffix = query.missionId
        ? `mission-${query.missionId.substring(0, 8)}`
        : 'all-missions';

      a.download = `audit-compliance-${suffix}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export compliance audit:', error);
      throw error;
    }
  }

  /**
   * Query assistant session summaries for audit page
   * Uses GET /v1/governance/audit/assistant-sessions
   * Sessions are identified by correlation_id and filtered by agent_id LIKE 'assistant:%'
   */
  async queryAssistantSessions(query: {
    /** A failed read throws instead of reading as an empty list (the next pages, ADR-20260925). */
    throwOnError?: boolean;
    startTime?: string;
    endTime?: string;
    limit?: number;
    offset?: number;
  }): Promise<AssistantSessionResponse> {
    try {
      const searchParams: Record<string, string | number> = {
        limit: query.limit || 50,
        offset: query.offset || 0,
      };

      if (query.startTime) searchParams['start_time'] = query.startTime;
      if (query.endTime) searchParams['end_time'] = query.endTime;

      const response = await unifiedApi.get<{
        data?: {
          items?: AssistantSessionSummary[];
          total?: number;
          limit?: number;
          offset?: number;
        };
      }>('governance/audit/assistant-sessions', searchParams);

      // Governance service wraps in {success, data, timestamp} — unwrap if present
      const data = response.data || {};
      return {
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total || 0,
        limit: data.limit || query.limit || 50,
        offset: data.offset || query.offset || 0,
      };
    } catch (error) {
      if (query.throwOnError) throw error;
      console.error('Failed to query assistant sessions:', error);
      return { items: [], total: 0, limit: 50, offset: 0 };
    }
  }

  /**
   * Query designer session summaries (agent_id LIKE 'mission-designer%').
   * Uses GET /v1/governance/audit/designer-sessions.
   */
  async queryDesignerSessions(query: {
    /** A failed read throws instead of reading as an empty list (the next pages, ADR-20260925). */
    throwOnError?: boolean;
    startTime?: string;
    endTime?: string;
    limit?: number;
    offset?: number;
  }): Promise<DesignerSessionResponse> {
    try {
      const searchParams: Record<string, string | number> = {
        limit: query.limit || 50,
        offset: query.offset || 0,
      };

      if (query.startTime) searchParams['start_time'] = query.startTime;
      if (query.endTime) searchParams['end_time'] = query.endTime;

      const response = await unifiedApi.get<{
        data?: {
          items?: DesignerSessionSummary[];
          total?: number;
          limit?: number;
          offset?: number;
        };
      }>('governance/audit/designer-sessions', searchParams);

      const data = response.data || {};
      return {
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total || 0,
        limit: data.limit || query.limit || 50,
        offset: data.offset || query.offset || 0,
      };
    } catch (error) {
      if (query.throwOnError) throw error;
      console.error('Failed to query designer sessions:', error);
      return { items: [], total: 0, limit: 50, offset: 0 };
    }
  }

  /**
   * Query resource-admin session summaries for the Resources audit tab.
   * Backed by mission_audit_structural rows where agent_id LIKE 'resource-admin:%'
   * — each row is one provider connect/disconnect/test, governance resource
   * CRUD, tool CRUD, or default-model setting change.
   * Uses GET /v1/governance/audit/resource-sessions.
   */
  async queryResourceSessions(query: {
    /** A failed read throws instead of reading as an empty list (the next pages, ADR-20260925). */
    throwOnError?: boolean;
    startTime?: string;
    endTime?: string;
    category?: string;
    action?: string;
    resourceId?: string;
    limit?: number;
    offset?: number;
  }): Promise<ResourceSessionResponse> {
    try {
      const searchParams: Record<string, string | number> = {
        limit: query.limit || 50,
        offset: query.offset || 0,
      };

      if (query.startTime) searchParams['start_time'] = query.startTime;
      if (query.endTime) searchParams['end_time'] = query.endTime;
      if (query.category) searchParams['category'] = query.category;
      if (query.action) searchParams['action'] = query.action;
      if (query.resourceId) searchParams['resource_id'] = query.resourceId;

      const response = await unifiedApi.get<{
        data?: {
          items?: ResourceSessionSummary[];
          total?: number;
          limit?: number;
          offset?: number;
        };
      }>('governance/audit/resource-sessions', searchParams);

      const data = response.data || {};
      return {
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total || 0,
        limit: data.limit || query.limit || 50,
        offset: data.offset || query.offset || 0,
      };
    } catch (error) {
      if (query.throwOnError) throw error;
      console.error('Failed to query resource sessions:', error);
      return { items: [], total: 0, limit: 50, offset: 0 };
    }
  }

  /**
   * Query promoted learnings for the audit page.
   *
   * This intentionally uses the promoted-learning read model rather than raw
   * episodes: these are the learnings currently eligible to be injected into
   * agent context, with canonical source audit lineage attached.
   */
  async queryLearningAudit(query: {
    /** A failed read throws instead of reading as an empty list (the next pages, ADR-20260925). */
    throwOnError?: boolean;
    startTime?: string;
    endTime?: string;
    limit?: number;
    offset?: number;
  }): Promise<LearningAuditResponse> {
    try {
      const searchParams: Record<string, string | number> = {
        limit: query.limit || 50,
        offset: query.offset || 0,
      };

      if (query.startTime) searchParams['start_time'] = query.startTime;
      if (query.endTime) searchParams['end_time'] = query.endTime;

      const response = await unifiedApi.get<{
        data?: {
          items?: LearningAuditEntry[];
          total?: number;
          limit?: number;
          offset?: number;
        };
      }>('governance/audit/learnings', searchParams);

      const data = response.data || {};
      return {
        items: Array.isArray(data.items) ? data.items : [],
        total: data.total || 0,
        limit: data.limit || query.limit || 50,
        offset: data.offset || query.offset || 0,
      };
    } catch (error) {
      if (query.throwOnError) throw error;
      console.error('Failed to query learning audit:', error);
      return { items: [], total: 0, limit: 50, offset: 0 };
    }
  }

  /**
   * WP-C10: Get learning applications for a specific learning episode.
   * Uses GET /v1/governance/audit/learnings/{id}/applications
   *
   * Returns the learning entry, aggregated application counts, and recent
   * applications (limit 25) for the learning detail page.
   */
  async getLearningApplications(
    learningEpisodeId: string,
    params?: { limit?: number; offset?: number; throwOnError?: boolean }
  ): Promise<LearningApplicationsResponse> {
    const defaultResponse: LearningApplicationsResponse = {
      learning: null,
      totalApplications: 0,
      successCount: 0,
      failureCount: 0,
      last7dApplications: 0,
      recentApplications: [],
    };

    try {
      const searchParams: Record<string, number> = {};
      if (params?.limit) searchParams['limit'] = params.limit;
      if (params?.offset) searchParams['offset'] = params.offset;

      const response = await unifiedApi.get<{
        data?: {
          learning?: LearningAuditEntry | null;
          totalApplications?: number;
          successCount?: number;
          failureCount?: number;
          last7dApplications?: number;
          recentApplications?: RecentApplication[];
        };
      }>(`governance/audit/learnings/${learningEpisodeId}/applications`, searchParams);

      const data = response.data || {};
      return {
        learning: data.learning ?? null,
        totalApplications: data.totalApplications ?? 0,
        successCount: data.successCount ?? 0,
        failureCount: data.failureCount ?? 0,
        last7dApplications: data.last7dApplications ?? 0,
        recentApplications: Array.isArray(data.recentApplications) ? data.recentApplications : [],
      };
    } catch (error) {
      if (params?.throwOnError) throw error;
      console.error('Failed to get learning applications:', error);
      return defaultResponse;
    }
  }

  /**
   * Get audit entries for a specific assistant session.
   *
   * Sessions are identified by execution_id (the JS code historically
   * called it correlationId, kept here for backwards compatibility with
   * the existing call sites). Assistant events are stored in
   * agion.mission_audit_structural alongside mission events for data
   * compatibility, so we reuse the QueryMissionAudit endpoint and filter
   * by execution_id rather than hitting the older execution_audit table.
   *
   * Uses GET /v1/governance/audit/mission-execution?execution_id={id}.
   */
  // throwOnError: the next pages show a failed read as a failure (ADR-20260925);
  // the current pages keep the empty result.
  async getAssistantSessionEvents(correlationId: string, options?: { throwOnError?: boolean }): Promise<MissionAuditEntry[]> {
    try {
      // Assistant events are stored in mission_audit_structural with
      // execution_id = session correlation ID. Reuse the mission audit
      // endpoint for data compatibility with mission events.
      const response = await unifiedApi.get<{ data?: { items?: MissionAuditEntry[] } }>(
        'governance/audit/mission-execution',
        { execution_id: correlationId, include_control_plane: 'true', limit: 200 }
      );

      const data = response.data || {};
      return Array.isArray(data.items) ? data.items : [];
    } catch (error) {
      if (options?.throwOnError) throw error;
      console.error('Failed to get assistant session events:', error);
      return [];
    }
  }

  /**
   * Get time range for predefined periods
   */
  getTimeRange(period: '1d' | '7d' | '30d' | '90d'): { start: Date; end: Date } {
    const end = new Date();
    const start = new Date();

    switch (period) {
      case '1d':
        start.setDate(start.getDate() - 1);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
    }

    return { start, end };
  }

  /**
   * Get intelligence KPI for audit dashboard or capsule detail page.
   * Uses GET /v1/governance/audit/kpi/intelligence
   *
   * Returns 4 stats (capsules/ingesting/failed/events24h) with 7-bucket trends.
   * When capsuleId is provided, returns capsule-scoped KPIs.
   */
  async getIntelligenceKpi(query: {
    startTime?: string;
    endTime?: string;
    capsuleId?: string;
  }): Promise<KpiResponse | null> {
    try {
      const searchParams: Record<string, string> = {};

      if (query.startTime) searchParams['start_time'] = query.startTime;
      if (query.endTime) searchParams['end_time'] = query.endTime;
      if (query.capsuleId) searchParams['capsule_id'] = query.capsuleId;

      const response = await unifiedApi.get<{
        data?: KpiResponse;
      }>('governance/audit/kpi/intelligence', searchParams);

      return response.data || null;
    } catch (error) {
      console.error('Failed to get intelligence KPI:', error);
      return null;
    }
  }

  /**
   * Get tab-scoped KPIs for the audit dashboard.
   * Uses GET /v1/governance/audit/kpi/{tab}
   *
   * WP-B2: Generic method to fetch KPIs for any audit tab.
   * Returns 4 stats with 7-bucket trend arrays for sparkline rendering.
   *
   * Tab stat mappings:
   * - missions: missions / executions / successRate% / tokens
   * - assistant: sessions / toolCalls / errors / p95LatencyMs
   * - designer: edits / autoRepairs / lifecycleChanges / drafts
   * - resources: resourcesChanged / providerEvents / defaultModelChanges / distinctActors
   * - learnings: active / promoted / appliedLast7d / avgConfidence
   * - intelligence: capsules / ingesting / failed / events24h
   */
  async getAuditKpi(
    tab: 'missions' | 'assistant' | 'designer' | 'resources' | 'learnings' | 'intelligence',
    query: {
      startTime?: string;
      endTime?: string;
    }
  ): Promise<KpiResponse | null> {
    try {
      return await this.readAuditKpi(tab, query);
    } catch (error) {
      console.error(`Failed to get ${tab} KPI:`, error);
      return null;
    }
  }

  /**
   * As getAuditKpi, but a failed call throws the adapter's failure, so the
   * caller can tell a refusal (403) from a fault (agion/Agion#2727).
   */
  async readAuditKpi(
    tab: 'missions' | 'assistant' | 'designer' | 'resources' | 'learnings' | 'intelligence',
    query: {
      startTime?: string;
      endTime?: string;
    }
  ): Promise<KpiResponse | null> {
    const searchParams: Record<string, string> = {};

    if (query.startTime) searchParams['start_time'] = query.startTime;
    if (query.endTime) searchParams['end_time'] = query.endTime;

    const response = await unifiedApi.get<{
      data?: KpiResponse;
    }>(`governance/audit/kpi/${tab}`, searchParams);

    return response.data || null;
  }

}

export const auditService = new AuditService();
